import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  SyntheticEvent,
} from 'react';
import {
  Card,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { TabContext } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAuthContext } from '../../contexts/AuthContext';
import { DateSelectionCard } from './components/DateSelectionCard';
import { MainActivityChart } from './components/MainActivityChart';
import { StatisticsTabPanel } from './components/StatisticsTabPanel';
import { QuickDateRangeType, useDateSelection } from './hooks/useDateSelection';
import { useStatisticsQueries } from './hooks/useStatisticsQueries';
import { useAccountActivityChart } from './hooks/useAccountActivityChart';
import { useJournalSorting } from './hooks/useJournalSorting';
import { useZoomDetection } from './hooks/useZoomDetection';
import { STATISTICS_TAB_CONFIG } from './config/tabConfig';
import {
  AggregationLevel,
  getDefaultAggregation,
} from './utils/dateAggregation';
import { useUsers } from '../../hooks/api/useUsers';
import { useBranding } from '../../hooks/api/useBranding';
import { hasExtension } from '../../utils/branding';
import { PageTitle } from '../../components/PageTitle';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels
);

function Statistics() {
  // Set ChartJS defaults inside the component to avoid early layout access
  useEffect(() => {
    ChartJS.defaults.font.family =
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ChartJS.defaults.font.size = 13;
    ChartJS.defaults.color = '#666';
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { account } = useAuthContext();
  const { accounts: accountList } = useUsers();
  const { branding } = useBranding();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCompact } = useZoomDetection();

  const visibleTabs = useMemo(
    () =>
      STATISTICS_TAB_CONFIG.filter(
        (tab) =>
          (!tab.adminOnly || account === 'admin') &&
          (!tab.requiredExtension ||
            hasExtension(branding, tab.requiredExtension))
      ),
    [account, branding]
  );

  const getInitialValues = useCallback(() => {
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');
    const tabParam = searchParams.get('tab');
    const aggParam = searchParams.get('agg');

    const initialStartDate = startParam ? dayjs(startParam) : undefined;
    const initialEndDate = endParam ? dayjs(endParam) : undefined;
    const initialTab = tabParam
      ? visibleTabs.findIndex((t) => t.key === tabParam)
      : 0;
    const initialAgg = aggParam || undefined;

    return {
      initialStartDate: initialStartDate?.isValid()
        ? initialStartDate
        : undefined,
      initialEndDate: initialEndDate?.isValid() ? initialEndDate : undefined,
      initialTab: initialTab >= 0 ? initialTab : 0,
      initialAgg,
    };
  }, [searchParams, visibleTabs]);

  const { initialStartDate, initialEndDate, initialTab, initialAgg } =
    getInitialValues();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [aggregationLevel, setAggregationLevel] = useState<
    AggregationLevel | undefined
  >(initialAgg as AggregationLevel | undefined);
  const [userSetAggregation, setUserSetAggregation] = useState(!!initialAgg);

  const {
    startDate,
    endDate,
    dateError,
    setStartDate,
    setEndDate,
    setQuickDateRange,
    quickDateRange,
    datePickerSlots,
  } = useDateSelection(initialStartDate, initialEndDate);

  const resetAggregation = useCallback(() => {
    setUserSetAggregation(false);
    setAggregationLevel(undefined);
  }, []);

  const handleQuickDateRange = useCallback(
    (type: QuickDateRangeType) => {
      setQuickDateRange(type);
      resetAggregation();
    },
    [setQuickDateRange, resetAggregation]
  );

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setStartDate(date);
      resetAggregation();
    },
    [setStartDate, resetAggregation]
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setEndDate(date);
      resetAggregation();
    },
    [setEndDate, resetAggregation]
  );

  const handleRangeNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      const monthsDiff = endDate.diff(startDate, 'month');
      const daysDiff = endDate.diff(startDate, 'day');

      let newStartDate: Dayjs;
      let newEndDate: Dayjs;

      const isStartAtMonthStart = startDate.date() === 1;
      const isEndAtMonthEnd = endDate.date() === endDate.endOf('month').date();
      const isFullMonths = isStartAtMonthStart && isEndAtMonthEnd;

      if (isFullMonths) {
        const monthsSpan =
          endDate.month() -
          startDate.month() +
          (endDate.year() - startDate.year()) * 12 +
          1;

        if (direction === 'prev') {
          newStartDate = startDate.subtract(monthsSpan, 'month');
          newEndDate = newStartDate.add(monthsSpan - 1, 'month').endOf('month');
        } else {
          newStartDate = endDate.add(1, 'day').startOf('month');
          newEndDate = newStartDate.add(monthsSpan - 1, 'month').endOf('month');
        }
      } else if (monthsDiff >= 1) {
        const approxMonths = Math.round(daysDiff / 30);

        if (direction === 'prev') {
          newEndDate = startDate.subtract(1, 'day').endOf('month');
          newStartDate = newEndDate
            .subtract(approxMonths - 1, 'month')
            .startOf('month');
        } else {
          newStartDate = endDate.add(1, 'day').startOf('month');
          newEndDate = newStartDate
            .add(approxMonths - 1, 'month')
            .endOf('month');
        }
      } else {
        if (direction === 'prev') {
          newStartDate = startDate.subtract(daysDiff + 1, 'day');
          newEndDate = endDate.subtract(daysDiff + 1, 'day');
        } else {
          newStartDate = startDate.add(daysDiff + 1, 'day');
          newEndDate = endDate.add(daysDiff + 1, 'day');
        }
      }

      const today = dayjs().endOf('day');
      if (direction === 'next' && newEndDate.isAfter(today)) {
        if (newStartDate.isAfter(today)) return;
        newEndDate = today;
        if (isFullMonths || monthsDiff >= 1) {
          const monthsBack = Math.round(daysDiff / 30);
          newStartDate = today
            .subtract(monthsBack - 1, 'month')
            .startOf('month');
        }
      }

      if (newStartDate.isAfter(newEndDate)) {
        newStartDate = newEndDate;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      resetAggregation();
    },
    [startDate, endDate, setStartDate, setEndDate, resetAggregation]
  );

  const queries = useStatisticsQueries(
    startDate,
    endDate,
    activeTab,
    visibleTabs
  );
  const accountActivityChart = useAccountActivityChart(
    queries.accountActivity.data,
    accountList
  );
  const { sortedJournals, order, orderBy, handleSort } = useJournalSorting(
    queries.journals.data?.journals || []
  );

  useEffect(() => {
    if (!userSetAggregation) {
      const newAggregation = getDefaultAggregation(startDate, endDate);
      if (newAggregation !== aggregationLevel) {
        setAggregationLevel(newAggregation);
      }
    }
  }, [startDate, endDate, userSetAggregation, aggregationLevel]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('start', startDate.format('YYYY-MM-DD'));
    params.set('end', endDate.format('YYYY-MM-DD'));

    if (visibleTabs[activeTab]) {
      params.set('tab', visibleTabs[activeTab].key);
    }

    if (aggregationLevel) {
      params.set('agg', aggregationLevel);
    }

    setSearchParams(params, { replace: true });
  }, [
    startDate,
    endDate,
    activeTab,
    aggregationLevel,
    visibleTabs,
    setSearchParams,
  ]);

  const handleTabChange = useCallback(
    (event: SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
      const tabName = visibleTabs[newValue]?.labelKey
        ? t(visibleTabs[newValue].labelKey)
        : '';
      if (tabName) {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = `${tabName} ${t(
          'statistics.tabSelected',
          'tab selected'
        )}`;
        document.body.appendChild(announcement);
        setTimeout(() => document.body.removeChild(announcement), 1000);
      }
    },
    [visibleTabs, t]
  );

  const handleAggregationChange = useCallback((level: AggregationLevel) => {
    setAggregationLevel(level);
    setUserSetAggregation(true);
  }, []);

  const getTabData = (tab: (typeof visibleTabs)[0]) => {
    const query = queries[tab.queryKey as keyof typeof queries];

    if (tab.key === 'accountActivity' && accountActivityChart) {
      return { ...query.data, chart: accountActivityChart };
    }

    return query?.data;
  };

  return (
    <>
      <PageTitle title={t('statistics.title') as string} />
      <Card
        variant="default"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={24}
          sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
        >
          <Box>
            <Typography component="h1" variant="h4" gutterBottom>
              {t('statistics.title')}
            </Typography>
          </Box>

          <section aria-label={String(t('statistics.title'))}>
            <DateSelectionCard
              startDate={startDate}
              endDate={endDate}
              dateError={dateError}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              onQuickDateRange={handleQuickDateRange}
              quickDateRange={quickDateRange}
              onRangeNavigate={handleRangeNavigate}
              datePickerSlots={datePickerSlots}
            />
          </section>

          <section
            id="statistics-main-chart"
            aria-label={String(t('statistics.totalGraph.title'))}
            tabIndex={-1}
          >
            <MainActivityChart
              data={queries.activity.data}
              isLoading={queries.activity.isLoading}
              startDate={startDate}
              endDate={endDate}
              aggregationLevel={aggregationLevel}
              onAggregationChange={handleAggregationChange}
              isMobile={isMobile || isCompact}
            />
          </section>

          <section
            aria-label={String(
              t('statistics.detailedData', 'Detailed statistics data')
            )}
          >
            <Box
              sx={{
                maxWidth: '100%',
              }}
            >
              <nav
                id="statistics-data-tabs"
                aria-label={String(
                  t('statistics.tabNavigation', 'Statistics categories')
                )}
                tabIndex={-1}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  selectionFollowsFocus
                  aria-label="Statistics data views"
                >
                  {visibleTabs.map((tab, index) => (
                    <Tab
                      key={tab.key}
                      value={index}
                      label={t(tab.labelKey)}
                      id={`statistics-tab-${index}`}
                      aria-controls={`statistics-tabpanel-${index}`}
                      aria-selected={activeTab === index}
                    />
                  ))}
                </Tabs>
              </nav>

              <TabContext value={activeTab.toString()}>
                {visibleTabs.map((tab, index) => {
                  if (index !== activeTab) return null;

                  const query = queries[tab.queryKey as keyof typeof queries];
                  const tabData = getTabData(tab);

                  return (
                    <StatisticsTabPanel
                      key={tab.key}
                      tab={tab}
                      index={index}
                      value={index.toString()}
                      data={tabData}
                      isLoading={query?.isLoading || false}
                      isError={query?.isError || false}
                      isMobile={isMobile}
                      sortedJournals={sortedJournals}
                      order={order}
                      orderBy={orderBy}
                      onSort={handleSort}
                    />
                  );
                })}
              </TabContext>
            </Box>
          </section>
        </Stack>
      </Card>
    </>
  );
}

export default Statistics;
